import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, finalize, map, mergeMap, retryWhen, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface ApiResponse<T = unknown> {
  value?: T;
  message?: string;
  isSuccess: boolean;
  errors?: string[];
  meta?: {
    page?: number;
    limit?: number;
    total?: number;
    hasMore?: boolean;
  };
}

export interface PaginatedApiResponse<T = unknown> {
  value?: PaginatedValue<T>;
  message?: string;
  isSuccess: boolean;
  errors?: string[];
  totalPages?: number;
}

export interface PaginatedValue<T = unknown> {
  filters?: string[];
  sorts?: string[];
  items: T[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages?: number;
}

export interface ApiError {
  message: string;
  code?: string;
  status: number;
  errors?: string[];
  warnings?: string[];
  draftId?: number | null;
}

export interface RequestOptions {
  params?:
    | HttpParams
    | Record<string, string | number | boolean | readonly (string | number | boolean)[]>;
  headers?: HttpHeaders | Record<string, string>;
  reportProgress?: boolean;
  responseType?: 'json' | 'text' | 'blob' | 'arraybuffer';
  withCredentials?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ApiClientService {
  private http = inject(HttpClient);
  private readonly baseUrl = environment.apiUrl || '/api';

  // Loading state signals
  private _isLoading = signal(false);
  private _activeRequests = signal(0);

  readonly isLoading = this._isLoading.asReadonly();
  readonly activeRequests = this._activeRequests.asReadonly();

  get<T>(endpoint: string, options?: RequestOptions): Observable<T> {
    return this.request<T>('GET', endpoint, null, options);
  }

  post<T>(endpoint: string, data: unknown, options?: RequestOptions): Observable<T> {
    return this.request<T>('POST', endpoint, data, options);
  }

  put<T>(endpoint: string, data: unknown, options?: RequestOptions): Observable<T> {
    return this.request<T>('PUT', endpoint, data, options);
  }

  patch<T>(endpoint: string, data: unknown, options?: RequestOptions): Observable<T> {
    return this.request<T>('PATCH', endpoint, data, options);
  }

  delete<T>(endpoint: string, options?: RequestOptions): Observable<T> {
    return this.request<T>('DELETE', endpoint, null, options);
  }

  deleteWithBody<T>(endpoint: string, data: unknown, options?: RequestOptions): Observable<T> {
    return this.request<T>('DELETE', endpoint, data, options);
  }

  upload<T>(endpoint: string, file: File, options?: RequestOptions): Observable<T> {
    const formData = new FormData();
    formData.append('file', file);

    return this.request<T>('POST', endpoint, formData, {
      ...options,
      headers: {
        ...options?.headers,
        // Don't set Content-Type for FormData, let browser set it with boundary
      },
    });
  }

  download(endpoint: string, filename?: string, options?: RequestOptions): Observable<Blob> {
    return this.request<Blob>('GET', endpoint, null, {
      ...options,
      responseType: 'blob',
    }).pipe(
      tap(blob => {
        if (filename && blob) {
          this.downloadBlob(blob, filename);
        }
      })
    );
  }

  private request<T>(
    method: string,
    endpoint: string,
    data: unknown,
    options?: RequestOptions
  ): Observable<T> {
    const url = this.buildUrl(endpoint);
    const requestOptions = this.buildRequestOptions(options);

    this.incrementActiveRequests();

    return this.http
      .request<ApiResponse<T>>(method, url, {
        body: data,
        ...requestOptions,
      })
      .pipe(
        retryWhen(errors => this.retryStrategy(errors)),
        catchError(error => this.handleError(error)),
        map((response: unknown) => {
          // Extract data from ApiResponse wrapper if it exists
          const apiResponse = response as { success?: boolean; data?: T };
          return apiResponse?.success !== undefined ? (apiResponse.data as T) : (response as T);
        }),
        finalize(() => this.decrementActiveRequests())
      ) as Observable<T>;
  }

  private buildUrl(endpoint: string): string {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${this.baseUrl}${cleanEndpoint}`;
  }

  private buildRequestOptions(options?: RequestOptions): Record<string, unknown> {
    const requestOptions: Record<string, unknown> = {
      observe: 'body',
      responseType: options?.responseType || 'json',
    };

    if (options?.params) {
      requestOptions['params'] =
        options.params instanceof HttpParams
          ? options.params
          : new HttpParams({ fromObject: options.params });
    }

    if (options?.headers) {
      requestOptions['headers'] =
        options.headers instanceof HttpHeaders ? options.headers : new HttpHeaders(options.headers);
    }

    if (options?.reportProgress) {
      requestOptions['reportProgress'] = true;
    }

    if (options?.withCredentials) {
      requestOptions['withCredentials'] = true;
    }

    return requestOptions;
  }

  private retryStrategy(errors: Observable<HttpErrorResponse>): Observable<number> {
    return errors.pipe(
      mergeMap((error: HttpErrorResponse, attempt: number) => {
        // Don't retry on client errors (4xx) except for timeouts
        if (error.status >= 400 && error.status < 500 && error.status !== 408) {
          return throwError(() => error);
        }

        // Don't retry more than 3 times
        if (attempt >= 3) {
          return throwError(() => error);
        }

        // Exponential backoff: 1s, 2s, 4s
        const delayMs = Math.pow(2, attempt) * 1000;
        return timer(delayMs);
      })
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    if (error.error instanceof Blob) {
      return this.readBlobAsText(error.error).pipe(
        map(responseText => {
          try {
            return JSON.parse(responseText);
          } catch {
            return responseText;
          }
        }),
        mergeMap(errorBody =>
          this.handleError(
            new HttpErrorResponse({
              error: errorBody,
              headers: error.headers,
              status: error.status,
              statusText: error.statusText,
              url: error.url ?? undefined,
            })
          )
        )
      );
    }

    let apiError: ApiError;

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      apiError = {
        message: 'A client-side error occurred',
        status: 0,
        code: 'CLIENT_ERROR',
      };
    } else {
      // Server-side error
      let errorBody = error.error;
      if (typeof errorBody === 'string') {
        try {
          errorBody = JSON.parse(errorBody);
        } catch {
          // Keep non-JSON response text available for the existing fallback message.
        }
      }

      // Check if this is a JSON parsing error (status 200 but malformed JSON)
      const isJsonParseError =
        error.status === 200 &&
        (typeof errorBody === 'string' ||
          (errorBody && typeof errorBody === 'object' && errorBody.message?.includes('JSON')));

      if (isJsonParseError) {
        apiError = {
          message: errorBody?.message || errorBody || 'Invalid JSON response from server',
          status: error.status,
          code: 'JSON_PARSE_ERROR',
          errors: undefined,
        };
      } else {
        const failureErrors = Array.isArray(errorBody?.failures)
          ? errorBody.failures.flatMap((failure: { errors?: unknown }) =>
              Array.isArray(failure?.errors)
                ? failure.errors.filter((item): item is string => typeof item === 'string')
                : []
            )
          : [];
        const responseErrors = errorBody?.errors || errorBody?.value?.errors;

        apiError = {
          message: errorBody?.error || errorBody?.message || `Server error: ${error.status}`,
          status: error.status,
          code: errorBody?.code || 'SERVER_ERROR',
          errors:
            Array.isArray(responseErrors) && responseErrors.length > 0
              ? responseErrors
              : failureErrors.length > 0
              ? failureErrors
              : undefined,
          warnings: errorBody?.warnings || errorBody?.value?.warnings,
          draftId: errorBody?.value?.draftId ?? errorBody?.draftId ?? null,
        };
      }
    }

    console.error('API Error:', apiError);
    return throwError(() => apiError);
  }

  private readBlobAsText(blob: Blob): Observable<string> {
    return new Observable<string>(subscriber => {
      const reader = new FileReader();
      reader.onload = () => {
        subscriber.next(typeof reader.result === 'string' ? reader.result : '');
        subscriber.complete();
      };
      reader.onerror = () => subscriber.error(reader.error);
      reader.readAsText(blob);

      return () => {
        if (reader.readyState === FileReader.LOADING) reader.abort();
      };
    });
  }

  private incrementActiveRequests(): void {
    const current = this._activeRequests();
    this._activeRequests.set(current + 1);
    this._isLoading.set(true);
  }

  private decrementActiveRequests(): void {
    const current = this._activeRequests();
    const newCount = Math.max(0, current - 1);
    this._activeRequests.set(newCount);
    this._isLoading.set(newCount > 0);
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // Utility methods for common API patterns
  getPaginated<T>(
    endpoint: string,
    page = 1,
    limit = 10,
    filters?: Record<string, string | number | boolean>
  ): Observable<ApiResponse<T[]>> {
    const params = {
      page: page.toString(),
      limit: limit.toString(),
      ...filters,
    };

    return this.get<ApiResponse<T[]>>(endpoint, { params });
  }

  search<T>(
    endpoint: string,
    query: string,
    options?: { page?: number; limit?: number; filters?: Record<string, string | number | boolean> }
  ): Observable<ApiResponse<T[]>> {
    const params = {
      q: query,
      page: options?.page?.toString() || '1',
      limit: options?.limit?.toString() || '10',
      ...options?.filters,
    };

    return this.get<ApiResponse<T[]>>(endpoint, { params });
  }
}
